// application/useCases/mintServiceCode.js
import { CODE_STRING_REJECTION, toCodeString } from '../../domain/services/codeString';
import { toAsOfDate } from '../../domain/services/effectiveDating';
import {
  SERVICE_CODE_DRAFT_ERRORS,
  validateServiceCodeDraft,
} from '../../domain/services/serviceCodeDraft';
import { CodeStringRejected } from '../ports/serviceCodeErrors';

/**
 * The draft errors this use case raises as a refused code string rather than as a programming error.
 *
 * The three of them are what a person can type; everything else in the vocabulary is a field the UI
 * would not have let them leave empty, so it arrives here only when a caller skipped the form.
 */
const CODE_STRING_REASON = {
  [SERVICE_CODE_DRAFT_ERRORS.CODE_REQUIRED]: CODE_STRING_REJECTION.EMPTY,
  [SERVICE_CODE_DRAFT_ERRORS.CODE_TOO_LONG]: CODE_STRING_REJECTION.TOO_LONG,
  [SERVICE_CODE_DRAFT_ERRORS.CODE_UNSTORABLE]: CODE_STRING_REJECTION.UNSTORABLE_CHARACTERS,
};

/**
 * Creates one org-authored code, after checking the two things that must be true before it exists.
 *
 * IT DOES NOT CONSULT THE SHADOW GUARD, and the omission is the design rather than a gap. The guard
 * advises and never blocks — a genuinely local `SC-DIRECT`, and an org whose funder really does bill
 * a code we happen to govern, are both legitimate — so the finding belongs on screen beside the
 * field, where a person can weigh it, and not in a rule that would have to decide. Enforcing it here
 * would make the advice a refusal by the back door.
 *
 * WHAT IT DOES ENFORCE is everything `validateServiceCodeDraft` decides — THE SAME RULES THE FORM'S
 * FIELDS HANG THEIR `:rules` OFF, read from the one place that owns them. A use case cannot trust
 * that a caller ran the form, and a second copy of the rules here is how the two come to disagree
 * about what a valid code is.
 *
 * IT STILL THROWS RATHER THAN RETURNING CODES, because its audience is different. The form turns a
 * code into a message beside a field; anything reaching here has already been past that, so a
 * failure is either a caller that skipped the form or a value no field could produce. `CodeStringRejected`
 * carries the reason for the three a person can actually type, and the rest are a `TypeError`.
 *
 * THE CODE STRING IS NORMALIZED HERE, ONCE, and the normalized value is what the adapter stores and
 * embeds in the document id. Normalizing in two places is how a record comes to disagree with its own
 * id; normalizing in the adapter only would let a domain rule compare against the raw form.
 *
 * `validFrom` IS NOT DEFAULTED. It is read with `toAsOfDate` rather than `toDateStr` for the reason
 * every write in this context is: `toDateStr` truncates a longer ISO string, so it would accept an
 * instant, and slicing one picks whatever zone it was written in. This value is caller-supplied from
 * a date field in this same app, so an instant is a programming error rather than a legacy shape.
 *
 * A FUTURE START IS LEGAL AND EXPECTED. An org told a code begins on 1 January records it in
 * December; the code lands not-yet-in-effect, is priceable ahead of its start, and simply is not
 * billable until the date arrives.
 *
 * @param {import('../ports/ServiceCodeRepository.port').MintServiceCodeRequest} request
 * @param {import('../ports/ServiceCodeRepository.port').ServiceCodeRepository} repository
 * @returns {Promise<string>} the new code's id
 * @throws {CodeStringRejected} the code string is empty, too long, or unstorable
 * @throws {TypeError} `validFrom` is not a 'YYYY-MM-DD' calendar date, or `unitBasis` is not a
 *   member of the vocabulary
 * @throws {import('../ports/serviceCodeErrors').ServiceCodeAlreadyExists} this clinic already holds
 *   that code starting on that date
 */
// eslint-disable-next-line import/prefer-default-export
export async function mintServiceCode({
  clinicId,
  code,
  descriptor,
  unitBasis,
  validFrom,
}, repository) {
  const errors = validateServiceCodeDraft({ code, descriptor, unitBasis, validFrom });
  const codeStringReason = errors.map((error) => CODE_STRING_REASON[error]).find(Boolean);

  if (codeStringReason !== undefined) {
    throw new CodeStringRejected(codeStringReason, code);
  }
  if (errors.length > 0) {
    throw new TypeError(
      `An org-authored code cannot be minted: ${errors.join(', ')}.`,
    );
  }
  // Normalized ONCE, here, and the normalized values are what the adapter stores and embeds in the
  // document id. Normalizing in two places is how a record comes to disagree with its own id.
  const startsOn = toAsOfDate(validFrom);

  return repository.mint({
    clinicId,
    code: toCodeString(code),
    // Trimmed but otherwise left as typed. A descriptor is the org's own words for what its funder
    // pays for, and case-folding it would be us editing their copy.
    descriptor: typeof descriptor === 'string' ? descriptor.trim() : '',
    unitBasis,
    validFrom: startsOn,
  });
} // mintServiceCode()
